import { useEffect, useState } from 'react';

import Decimal from 'decimal.js';
import { useYearQuarterNavigation } from 'hooks';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useRecoilValue } from 'recoil';

import { displayNumber } from 'utils/misc';

import { ScenarioFlexCapacitySettingsResponse } from 'RestAPIs/scenario';

import { scenarioAtom } from '../../../atoms';

import { Box, Button, Grid, TextField, Typography } from '@mui/material';

import { useUpdateFlexCapacitySettings } from '../queries';

type Props = {
  data?: ScenarioFlexCapacitySettingsResponse;
};

const FlexCapacityAgreements = ({ data }: Props) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);
  const { year } = useYearQuarterNavigation();

  const [flexPercentage, setFlexPercentage] = useState<number | null>(null);

  useEffect(() => {
    if (typeof data?.flexCapacityPercentage === 'number') {
      setFlexPercentage(
        Decimal.mul(data.flexCapacityPercentage, 100).toNumber()
      );
    }
  }, [data?.flexCapacityPercentage]);

  const hasFlexPercentage = () => typeof flexPercentage === 'number';

  const { mutate: updateScenarioFlexCapacitySettings } =
    useUpdateFlexCapacitySettings();

  const onClickSave = async () => {
    if (!scenario || !data || flexPercentage === null) {
      return;
    }

    try {
      await updateScenarioFlexCapacitySettings({
        scenarioId: scenario.scenarioId,
        year,
        payload: {
          flexCapacityPercentage: Decimal.div(flexPercentage, 100).toNumber()
        }
      });
    } catch {
      setFlexPercentage(
        Decimal.mul(data.flexCapacityPercentage, 100).toNumber()
      ); // Reset to what it was
    }
  };

  return (
    <Box mt={2} mb={4}>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Grid container spacing={2} display="flex" alignItems="flex-end">
            <Grid item xs={6}>
              <NumericFormat
                customInput={TextField}
                value={flexPercentage ?? ''}
                onValueChange={({ floatValue }) => {
                  if (typeof floatValue === 'number') {
                    setFlexPercentage(floatValue);
                  }
                }}
                suffix="%"
                disabled={!hasFlexPercentage()}
                label={t('formation.agreedFlexPercentage')}
                fullWidth
              />
            </Grid>
            <Grid item xs="auto">
              <Button
                variant="outlined"
                onClick={onClickSave}
                size="small"
                disabled={!hasFlexPercentage()}
              >
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={7}>
          {data && (
            <Grid container>
              <Grid item xs={12} textAlign="right">
                <Typography>
                  {t('formation.totalAvailableFlexCapacity')}:{' '}
                  {t('amountHours', {
                    hours: displayNumber(data.flexCapacity)
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} textAlign="right">
                <Typography>
                  {t('formation.flexCapacityBalance')}:{' '}
                  {t('amountHours', {
                    hours: displayNumber(data.flexCapacityBalance)
                  })}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FlexCapacityAgreements;
